// Shared admin-managed photo carousel.
//
// Reads ordered photos from `useSlotPhotos(slotKey)` and renders them in a
// fixed-aspect Embla carousel so every slide looks uniform regardless of
// source photo dimensions. Autoplays with pause-on-hover; click a slide to
// open a lightbox. Renders nothing if the slot has no approved photos.
import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { ArrowLeft, ArrowRight, ChevronLeft, ChevronRight, Film, X } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useSlotPhotos } from "@/lib/slotPhotos";
import { useRecordUsage } from "@/lib/mediaUsage";
import { cn } from "@/lib/utils";

type Aspect = "16/9" | "4/3" | "1/1";
type PerView = { base: number; sm?: number; lg?: number };
type Background = "background" | "offwhite";

interface Props {
  slotKey: string;
  route: string;
  pageLabel: string;
  /** Used by media usage tracking — short label of the slot location on the page. */
  slot: string;
  aspect?: Aspect;
  perView?: PerView;
  eyebrow?: string;
  heading?: string;
  body?: string;
  background?: Background;
  /** Vertical padding preset. Default "lg". */
  spacing?: "sm" | "md" | "lg";
  /** Autoplay delay ms. Default 6000. Set to 0 to disable. */
  autoplayMs?: number;
}

const aspectClass = (a: Aspect) =>
  a === "16/9" ? "aspect-[16/9]" : a === "4/3" ? "aspect-[4/3]" : "aspect-square";

const PhotoSlider = ({
  slotKey,
  route,
  pageLabel,
  slot,
  aspect = "16/9",
  eyebrow,
  heading,
  body,
  background = "background",
  spacing = "lg",
  autoplayMs = 6000,
}: Props) => {
  const { data: photos } = useSlotPhotos(slotKey);
  useRecordUsage(photos?.map((p) => p.id) ?? [], route, pageLabel, slot);

  const autoplay = autoplayMs > 0 ? [Autoplay({ delay: autoplayMs, stopOnInteraction: false, stopOnMouseEnter: true })] : [];
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start" }, autoplay);

  const [selected, setSelected] = useState(0);
  const [lightbox, setLightbox] = useState<number | null>(null);
  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelected(emblaApi.selectedScrollSnap());
  }, [emblaApi]);
  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi, onSelect]);

  const list = photos ?? [];
  if (list.length < 2) return null;

  const sectionPad =
    spacing === "sm" ? "py-10 sm:py-12" : spacing === "md" ? "py-12 sm:py-16" : "py-14 sm:py-20 lg:py-24";
  const bgClass = background === "offwhite" ? "bg-offwhite" : "bg-background";

  const hasCopy = Boolean(eyebrow || heading || body);

  const goPrev = () => emblaApi?.scrollPrev();
  const goNext = () => emblaApi?.scrollNext();

  const sliderEl = (
    <div className="relative">
      <div ref={emblaRef} className="overflow-hidden rounded-lg">
        <div className="flex">
          {list.map((p, i) => (
            <div key={p.id} className="min-w-0 shrink-0 grow-0 basis-full">
              <button
                type="button"
                onClick={() => setLightbox(i)}
                aria-label={`View photo ${i + 1} of ${list.length}: ${p.alt_text ?? "Modern Fence Install project"}`}
                className="group relative block w-full overflow-hidden rounded-lg border border-border bg-muted shadow-xs hover:shadow-md transition-shadow"
              >
                <div className={cn("relative w-full", aspectClass(aspect))}>
                  <img
                    src={p.media_type === "video" ? p.poster_public_url ?? p.public_url : p.public_url}
                    alt={p.alt_text ?? "Modern Fence Install project"}
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                  />
                  {p.media_type === "video" && (
                    <span className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-sm bg-charcoal/85 text-charcoal-foreground px-2 py-0.5 text-[10px] font-medium">
                      <Film className="w-3 h-3" /> Video
                    </span>
                  )}
                </div>
                {p.caption && (
                  <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-charcoal/80 to-transparent text-charcoal-foreground text-xs px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    {p.caption}
                  </span>
                )}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Arrows */}
      <button
        type="button"
        onClick={goPrev}
        aria-label="Previous slide"
        className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-background/95 border border-border shadow-sm flex items-center justify-center text-foreground hover:bg-background"
      >
        <ArrowLeft className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={goNext}
        aria-label="Next slide"
        className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-background/95 border border-border shadow-sm flex items-center justify-center text-foreground hover:bg-background"
      >
        <ArrowRight className="w-4 h-4" />
      </button>

      {/* Dots */}
      <div className="flex justify-center gap-2 mt-4">
        {list.map((_, i) => (
          <button
            key={i}
            type="button"
            aria-label={`Go to slide ${i + 1}`}
            onClick={() => emblaApi?.scrollTo(i)}
            className={cn(
              "h-1.5 rounded-full transition-all",
              i === selected ? "w-6 bg-primary" : "w-2 bg-muted-foreground/30 hover:bg-muted-foreground/60",
            )}
          />
        ))}
      </div>
    </div>
  );

  const lightboxPhoto = lightbox !== null ? list[lightbox] : null;

  return (
    <section className={cn(sectionPad, bgClass)} aria-label={heading ?? "Photo slider"}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {hasCopy ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="order-2 lg:order-1">
              {eyebrow && (
                <p className="text-primary font-semibold text-sm uppercase tracking-widest mb-3">{eyebrow}</p>
              )}
              {heading && (
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-3">{heading}</h2>
              )}
              {body && <p className="text-muted-foreground text-base sm:text-lg">{body}</p>}
            </div>
            <div className="order-1 lg:order-2">{sliderEl}</div>
          </div>
        ) : (
          <div className="max-w-3xl mx-auto">{sliderEl}</div>
        )}
      </div>

      {/* Lightbox */}
      <Dialog open={lightbox !== null} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 bg-charcoal border-charcoal-foreground/10 overflow-hidden">
          {lightboxPhoto && (
            <div className="relative flex items-center justify-center">
              {lightboxPhoto.media_type === "video" ? (
                <video
                  src={lightboxPhoto.public_url}
                  poster={lightboxPhoto.poster_public_url ?? undefined}
                  controls
                  autoPlay
                  className="max-w-full max-h-[85vh]"
                />
              ) : (
                <img
                  src={lightboxPhoto.public_url}
                  alt={lightboxPhoto.alt_text ?? ""}
                  className="max-w-full max-h-[85vh] object-contain"
                />
              )}
              <button
                type="button"
                onClick={() => setLightbox(lightbox === 0 ? list.length - 1 : (lightbox ?? 0) - 1)}
                aria-label="Previous"
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-charcoal/70 hover:bg-charcoal/90 text-charcoal-foreground rounded-full p-2"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                type="button"
                onClick={() => setLightbox(lightbox === list.length - 1 ? 0 : (lightbox ?? 0) + 1)}
                aria-label="Next"
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-charcoal/70 hover:bg-charcoal/90 text-charcoal-foreground rounded-full p-2"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
              <button
                type="button"
                onClick={() => setLightbox(null)}
                aria-label="Close"
                className="absolute top-2 right-2 bg-charcoal/70 hover:bg-charcoal/90 text-charcoal-foreground rounded-full p-2"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 text-charcoal-foreground/80 text-sm bg-charcoal/60 px-3 py-1 rounded-full">
                {(lightbox ?? 0) + 1} / {list.length}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default PhotoSlider;
